"use client";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Trash2, X } from "lucide-react";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";

import { useLocationContext } from "../../contexts/locationContext";
import { handleGetPublicPitches } from "../../shared/controllers/pitchController";
import { handleUserInitializeCheckout } from "../../userDashboard/userControllers/shopController";
import AddressInput from "../../utils/addressInput";
import Payform from "../../utils/payForm";
import { formatItemPrice, formatPriceValue, resolveCurrencyDetails } from "../../utils/price";
import SavedCards, { Card } from "../../utils/savedCards";
import transactionReconfirmationService from "../../utils/transactionReconfirmation";
import WalletPayment from "../../utils/WalletPayment";

import check from "./assets/check.svg";
import load from "./assets/load.gif";
import OnboardModal from "./OnboardModal";

const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
);

interface Product {
    id: number;
    name: string;
    price: string | number;
    media?: {
        file_path: string;
    }[];
    [key: string]: unknown;
}

interface CartProps {
    showCart: boolean;
    setShowCart: (show: boolean) => void;
    cart: Record<string, number>;
    products: Product[];
    addToCart: (productId: number) => void;
    decreaseQuantity: (productId: number) => void;
    removeFromCart: (productId: number) => void;
    where: "landing" | "dashboard";
    clearCart: () => void;
    setShowThankYouModal: (show: boolean) => void;
    setCart: (cart: Record<string, number>) => void;
}

interface DeliveryOption {
    delivery_type: "pickup" | "delivery";
    address: string;
    pitch_id: number | null;
    [key: string]: unknown;
}

const Cart: React.FC<CartProps> = ({
    showCart,
    setShowCart,
    cart,
    products,
    addToCart,
    decreaseQuantity,
    removeFromCart,
    where,
    clearCart,
    setShowThankYouModal,
    setCart,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showPayform, setShowPayform] = useState(false);
    const [savedCards, setSavedCards] = useState<Card[]>([]);
    const [clientSecret, setClientSecret] = useState("");
    const [transactionID, setTransactionID] = useState("");
    const [isTransactionInitialized, setTransactionInitialized] = useState(false);
    const payment_method = "card";
    const [paymentOption, setPaymentOption] = useState("card");
    const [grand_total, setGrand_total] = useState("");

    const { location: storedLocation } = useLocationContext();
    const [initialCartSnapshot, setInitialCartSnapshot] = useState<string | null>(null);
    const [centers, setCenters] = useState<Record<string, unknown>[]>([]);
    const [loadingCenters, setLoadingCenters] = useState(true);
    const [deliveryOptions, setDeliveryOptions] = useState<Record<string, DeliveryOption>>({});
    const [applyToAll, setApplyToAll] = useState(false);

    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        const savedCart = localStorage.getItem("cart");
        if (!savedCart) return;

        try {
            const parsedCart = JSON.parse(savedCart);
            if (
                Object.keys(cart).length === 0 ||
                JSON.stringify(cart) !== savedCart
            ) {
                setCart(parsedCart);
            }
        } catch (error) {
            console.error("Error parsing cart from localStorage:", error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCart]);

    useEffect(() => {
        localStorage.setItem("cart", JSON.stringify(cart));
    }, [cart]);

    const fetchPitches = async () => {
        setLoadingCenters(true);
        try {
            const data = await handleGetPublicPitches();
            if (data) {
                const centersData = Array.isArray(data.data) ? data.data : [];
                setCenters(centersData);
            }
        } catch (error) {
            console.error("Error fetching centers:", error);
        } finally {
            setLoadingCenters(false);
        }
    };

    useEffect(() => {
        if (where === "dashboard") {
            fetchPitches();
        }
    }, [where]);

    useEffect(() => {
        if (where !== "dashboard" || loadingCenters) return;

        const updatedOptions = { ...deliveryOptions };
        Object.keys(cart).forEach((productId) => {
            const option = updatedOptions[productId];
            if (!option) {
                updatedOptions[productId] = {
                    delivery_type: "pickup",
                    address: "",
                    pitch_id: centers.length > 0 ? (centers[0] as Record<string, unknown>).id as number : null,
                };
                return;
            }
            if (option.delivery_type === "pickup") {
                option.pitch_id =
                    centers.length > 0 ? (centers[0] as Record<string, unknown>).id as number : null;
            }
        });
        Object.keys(updatedOptions).forEach((productId) => {
            if (!cart[productId]) {
                delete updatedOptions[productId];
            }
        });
        setDeliveryOptions(updatedOptions);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart, centers, where, loadingCenters]);



    const getCartTotal = () => {
        return Object.entries(cart).reduce((total, [productId, quantity]) => {
            const product = products.find((p) => p.id === Number.parseInt(productId));
            const deliveryCost =
                where === "dashboard" &&
                    deliveryOptions[productId]?.delivery_type === "delivery"
                    ? 5.99
                    : 0;
            return (
                total +
                (product ? Number.parseFloat(product.price.toString()) * quantity + deliveryCost : 0)
            );
        }, 0);
    };

    const getCartItems = () => {
        return Object.entries(cart)
            .map(([productId, quantity]) => {
                const product = products.find((p) => p.id === Number.parseInt(productId));
                return product ? { ...product, quantity } : null;
            })
            .filter((item): item is Product & { quantity: number } => item !== null);
    };

    const cartItems = getCartItems();
    const currencyDetails = resolveCurrencyDetails(
        cartItems[0],
        (storedLocation as { countryCode?: string })?.countryCode
    );
    const total = getCartTotal();

    const transformCartToExtras = () => {
        return Object.entries(cart).map(([productId, quantity]) => {
            const deliveryInfo =
                where === "dashboard"
                    ? deliveryOptions[productId] || {
                        delivery_type: "pickup",
                        address: "",


                        pitch_id: centers.length > 0 ? (centers[0] as { id?: number }).id : null,
                    }
                    : { delivery_type: "pickup", address: "", pitch_id: null };
            return {
                id: Number.parseInt(productId),
                quantity: quantity,
                delivery_type: deliveryInfo.delivery_type,
                address:
                    deliveryInfo.delivery_type === "delivery"
                        ? deliveryInfo.address
                        : undefined,
                pitch_id:
                    deliveryInfo.delivery_type === "pickup"
                        ? deliveryInfo.pitch_id
                        : null,
            };
        });
    };

    const onSuccess = (response: unknown) => {
        const res = response as { data: { message: { transaction: { client_secret: string; identifier: string }; saved_cards: Card[]; grand_total: string } } };
        setLoading(false);
        setClientSecret(res.data.message.transaction.client_secret);
        setSavedCards(res.data.message.saved_cards);
        setTransactionID(res.data.message.transaction.identifier);
        setGrand_total(res.data.message.grand_total);
        setTransactionInitialized(true);
        setInitialCartSnapshot(JSON.stringify(cart));
    };

    const onError = () => {
        setLoading(false);
    };

    const handleDeliveryChange = (productId: number, deliveryType: "pickup" | "delivery") => {
        setDeliveryOptions((prev) => {
            const updatedOptions: Record<string, DeliveryOption> = {
                ...prev,
                [productId.toString()]: {
                    ...prev[productId.toString()],
                    delivery_type: deliveryType,
                    pitch_id:
                        deliveryType === "pickup" && centers.length > 0

                            ? prev[productId.toString()]?.pitch_id || (centers[0] as { id?: number }).id
                            : null,
                } as DeliveryOption,
            };

            if (applyToAll) {
                const address = updatedOptions[productId.toString()]?.address;
                const pitchId = updatedOptions[productId.toString()]?.pitch_id;
                Object.keys(updatedOptions).forEach((id) => {
                    const option = updatedOptions[id];
                    if (option) {
                        updatedOptions[id] = {
                            delivery_type: deliveryType,
                            address: deliveryType === "delivery" ? address || "" : "",
                            pitch_id: deliveryType === "pickup" ? pitchId || null : null,
                        };
                    }
                });
            }

            return updatedOptions;
        });
    };

    const handleAddressChange = (productId: string | number, address: string) => {
        setDeliveryOptions((prev) => {
            const updatedOptions: Record<string, DeliveryOption> = {
                ...prev,
                [productId.toString()]: {
                    ...prev[productId.toString()],
                    address,
                } as DeliveryOption,
            };
            if (applyToAll) {
                Object.keys(updatedOptions).forEach((id) => {
                    const option = updatedOptions[id];
                    if (option && option.delivery_type === "delivery") {
                        option.address = address;
                    }
                });
            }
            return updatedOptions;
        });
    };

    const handlePitchChange = (productId: number, pitchId: string) => {
        setDeliveryOptions((prev) => {
            const updatedOptions: Record<string, DeliveryOption> = {
                ...prev,
                [productId.toString()]: {
                    ...prev[productId.toString()],
                    pitch_id: Number.parseInt(pitchId),
                } as DeliveryOption,
            };

            if (applyToAll) {
                Object.keys(updatedOptions).forEach((id) => {
                    const option = updatedOptions[id];
                    if (option && option.delivery_type === "pickup") {
                        option.pitch_id = Number.parseInt(pitchId);
                    }
                });
            }

            return updatedOptions;
        });
    };

    const validateDeliveryOptions = () => {
        if (where !== "dashboard") return true;

        for (const [productId, options] of Object.entries(deliveryOptions)) {
            const product = products.find((p) => p.id === Number.parseInt(productId));
            if (options.delivery_type === "delivery" && !options.address.trim()) {
                enqueueSnackbar(
                    `Please enter a delivery address for item "${product?.name || productId
                    }".`,
                    { variant: "error" }
                );
                return false;
            }
            if (options.delivery_type === "pickup" && !options.pitch_id) {
                enqueueSnackbar(
                    `Please select a SACA center for item "${product?.name || productId
                    }".`,
                    { variant: "error" }
                );
                return false;
            }
        }
        return true;
    };

    const handleSubmit = () => {
        if (!validateDeliveryOptions()) return;
        const extras = transformCartToExtras();
        setLoading(true);
        const userData = { extras };
        handleUserInitializeCheckout(userData, onSuccess, onError);
    };

    const handleCheckout = () => {
        if (where === "landing") {
            setShowModal(true);
        } else if (where === "dashboard") {
            handleSubmit();
        }
    };

    useEffect(() => {
        if (isTransactionInitialized && initialCartSnapshot) {
            const currentCartSnapshot = JSON.stringify(cart);
            if (currentCartSnapshot !== initialCartSnapshot) {
                setTransactionInitialized(false);
                setClientSecret("");
                setTransactionID("");
                setSavedCards([]);
                setShowPayform(false);
                setGrand_total("");
                setInitialCartSnapshot(null);
            }
        }
    }, [cart, isTransactionInitialized, initialCartSnapshot]);

    const resetAll = () => {
        setShowThankYouModal(true);
        setTransactionInitialized(false);
        setClientSecret("");
        setTransactionID("");
        setSavedCards([]);
        setShowPayform(false);
        setGrand_total("");
        clearCart();
        setInitialCartSnapshot(null);
        setShowCart(false);
        setDeliveryOptions({});
        setApplyToAll(false);
        localStorage.removeItem("cart");
    };

    if (!showCart) return null;

    return (
        <div className="fixed top-0 right-0 h-full bg-white shadow-lg z-[100] w-full sm:w-80 md:w-96 flex flex-col">
            <div className="p-4 border-b flex justify-between items-center h-[72px]">
                <h2 className="text-xl font-bold">Cart</h2>
                <button
                    onClick={() => setShowCart(false)}
                    className="text-gray-500 hover:text-gray-800"
                    aria-label="Close cart"
                >
                    <X size={20} />
                </button>
            </div>
            <div className="flex-grow overflow-y-auto p-4">
                {cartItems.length === 0 ? (
                    <div className="text-center py-8">
                        <p>Your cart is empty</p>
                    </div>
                ) : (
                    <>
                        {where === "dashboard" &&
                            !isTransactionInitialized &&
                            cartItems.length > 1 && (
                                <CartApplyToAll
                                    applyToAll={applyToAll}
                                    setApplyToAll={setApplyToAll}
                                    cartItems={cartItems}
                                    deliveryOptions={deliveryOptions}
                                    setDeliveryOptions={setDeliveryOptions}
                                    centers={centers}
                                />
                            )}

                        <div className="space-y-6">
                            {cartItems.map((item, index) => (
                                <CartItem
                                    key={item.id}
                                    item={item}
                                    index={index}
                                    where={where}
                                    isTransactionInitialized={isTransactionInitialized}
                                    deliveryOptions={deliveryOptions}
                                    applyToAll={applyToAll}
                                    cartItems={cartItems}
                                    centers={centers}
                                    loadingCenters={loadingCenters}
                                    addToCart={addToCart}
                                    decreaseQuantity={decreaseQuantity}
                                    removeFromCart={removeFromCart}
                                    handleDeliveryChange={handleDeliveryChange}
                                    handleAddressChange={handleAddressChange}
                                    handlePitchChange={handlePitchChange}
                                    storedLocation={storedLocation}
                                />
                            ))}
                        </div >

                        {isTransactionInitialized && (
                            <>
                                <div className="flex items-center mt-6 mb-2">
                                    <button
                                        onClick={() => {
                                            setTransactionInitialized(false);
                                            setClientSecret("");
                                            setTransactionID("");
                                            setSavedCards([]);
                                            setShowPayform(false);
                                            setGrand_total("");
                                        }}
                                        className="text-[#317333] hover:underline font-medium flex text-xs font-Manrope items-center"
                                    >
                                        <span className="mr-2">←</span> Back to Checkout
                                    </button>
                                </div>

                                <div className="mt-4 mb-4">
                                    <div className="flex w-full border-b border-[#EAEBF0]">
                                        <button
                                            className={`flex-1 p-4 font-Manrope font-medium text-sm ${paymentOption === "card"
                                                ? "border-b-2 border-[#317333] text-[#317333]"
                                                : "text-[#272D37]"
                                                }`}
                                            onClick={() => setPaymentOption("card")}
                                        >
                                            Payment Card
                                        </button>
                                        <button
                                            className={`flex-1 p-4 font-Manrope font-medium text-sm ${paymentOption === "wallet"
                                                ? "border-b-2 border-[#317333] text-[#317333]"
                                                : "text-[#272D37]"
                                                }`}
                                            onClick={() => setPaymentOption("wallet")}
                                        >
                                            Your Wallet
                                        </button>
                                    </div>
                                </div>

                                <div className="w-full overflow-y-auto">
                                    {paymentOption === "card" ? (
                                        <CardPaymentSection
                                            showPayform={showPayform}
                                            setShowPayform={setShowPayform}
                                            clientSecret={clientSecret}
                                            stripePromise={stripePromise}
                                            savedCards={savedCards}
                                            transactionID={transactionID}
                                            payment_method={payment_method}
                                            currencyDetails={currencyDetails}
                                            resetAll={resetAll}
                                        />
                                    ) : (
                                        <WalletPayment
                                            gateway={{
                                                id: 'wallet',
                                                name: 'Wallet',
                                                payment_type: 'Wallet',
                                                total: Number.parseFloat(grand_total) || 0,
                                                currency: currencyDetails.currencyCode || 'NGN',
                                                is_suggested: true,
                                                file: 'wallet.svg',
                                                details: {
                                                    currency: currencyDetails.currencyCode || 'NGN',
                                                    country_code: currencyDetails.countryCode || undefined,
                                                },
                                            }}
                                            amount={Number.parseFloat(grand_total) || 0}
                                            transactionId={transactionID}
                                            onSuccess={resetAll}
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div >

            {
                cartItems.length > 0 && !isTransactionInitialized && (
                    <div className="p-4 border-t mt-auto">
                        <div className="flex justify-between items-center font-medium text-lg mb-4">
                            <span>Total</span>
                            <span>{formatPriceValue(total, currencyDetails)}</span>
                        </div >

                        <button
                            className="w-full bg-[#317333] text-white py-3 rounded hover:bg-green-700 flex items-center justify-center"
                            onClick={handleCheckout}
                            disabled={loading || (where === "dashboard" && loadingCenters)}
                        >
                            {loading ? (
                                <img src={load} className="w-5" alt="Loading" />
                            ) : (
                                "Checkout"
                            )}
                        </button>
                    </div >
                )
            }

            {showModal && <OnboardModal onClose={() => setShowModal(false)} />}
        </div >
    );
};

const CartApplyToAll: React.FC<{
    applyToAll: boolean;
    setApplyToAll: (val: boolean) => void;
    cartItems: (Product & { quantity: number })[];
    deliveryOptions: Record<string, DeliveryOption>;
    setDeliveryOptions: React.Dispatch<React.SetStateAction<Record<string, DeliveryOption>>>;
    centers: Record<string, unknown>[];
}> = ({ applyToAll, setApplyToAll, cartItems, deliveryOptions, setDeliveryOptions, centers }) => {
    const handleToggle = (checked: boolean) => {
        setApplyToAll(checked);
        if (checked && cartItems.length > 0) {
            const firstItemId = cartItems[0]?.id;
            if (!firstItemId) return;

            const firstDeliveryInfo = deliveryOptions[firstItemId.toString()];
            if (!firstDeliveryInfo) return;

            const updatedOptions = { ...deliveryOptions };
            Object.keys(updatedOptions).forEach((id) => {
                updatedOptions[id] = {
                    delivery_type: firstDeliveryInfo.delivery_type,
                    address: firstDeliveryInfo.delivery_type === "delivery" ? firstDeliveryInfo.address || "" : "",
                    pitch_id: firstDeliveryInfo.delivery_type === "pickup" ? firstDeliveryInfo.pitch_id || null : null,
                };
            });
            setDeliveryOptions(updatedOptions);
        }
    };

    const firstItem = cartItems[0];
    const firstDeliveryInfo = firstItem ? deliveryOptions[firstItem.id.toString()] : undefined;

    return (
        <div className="mb-4">
            <label className="flex items-center space-x-2">
                <input
                    type="checkbox"
                    checked={applyToAll}
                    onChange={(e) => handleToggle(e.target.checked)}
                    className="w-4 h-4 text-[#317333] border-gray-300 focus:ring-[#317333]"
                />
                <span className="text-sm text-gray-700">Use this address for all items</span>
            </label>
            {applyToAll && firstItem && firstDeliveryInfo && (
                <div className="mt-2 p-3 bg-gray-100 rounded-md text-sm text-gray-700">
                    <p className="font-medium">Applied to all items:</p>
                    {firstDeliveryInfo.delivery_type === "delivery" ? (
                        <p>Deliver to: {firstDeliveryInfo.address || "Not specified"}</p>
                    ) : (
                        <p>Pick up at: {String(centers.find((c) => c.id === (firstDeliveryInfo.pitch_id || undefined))?.name || "Not specified")} (Free)</p>
                    )}
                </div>
            )}
        </div>
    );
};

interface CartItemProps {
    item: Product & { quantity: number };
    index: number;
    where: string;
    isTransactionInitialized: boolean;
    deliveryOptions: Record<string, DeliveryOption>;
    applyToAll: boolean;
    cartItems: unknown[];
    centers: Record<string, unknown>[];
    loadingCenters: boolean;
    addToCart: (id: number) => void;
    decreaseQuantity: (id: number) => void;
    removeFromCart: (id: number) => void;
    handleDeliveryChange: (id: number, type: "pickup" | "delivery") => void;
    handleAddressChange: (id: number | string, addr: string) => void;
    handlePitchChange: (id: number, pitchId: string) => void;
    storedLocation: unknown;
}

const CartItem: React.FC<CartItemProps> = ({
    item, index, where, isTransactionInitialized, deliveryOptions, applyToAll,
    cartItems, centers, loadingCenters, addToCart, decreaseQuantity, removeFromCart,
    handleDeliveryChange, handleAddressChange, handlePitchChange, storedLocation
}) => {
    const deliveryInfo = deliveryOptions[item.id.toString()] || {
        delivery_type: "pickup",
        address: "",
        pitch_id: centers.length > 0 ? (centers[0] as Record<string, unknown>).id as number : null,
    };

    const getProductImage = (product: Product) => {
        return product.media?.[0]?.file_path ?? null;
    };

    const isFirstItem = index === 0;
    const showDeliveryOptions = where === "dashboard" && (isFirstItem || !applyToAll || cartItems.length === 1);

    return (
        <div className="pb-4 border-b">
            <div className="flex items-center">
                <div className="w-16 h-16 mr-4 flex-shrink-0">
                    <img
                        src={getProductImage(item) || undefined}
                        alt={item.name}
                        className="w-full h-full object-cover rounded-md"
                    />
                </div>
                <div className="flex-grow min-w-0">
                    <h3 className="font-medium text-sm truncate">{item.name}</h3>
                    <p className="text-gray-700 font-bold">
                        {formatItemPrice(item, (storedLocation as { countryCode?: string })?.countryCode)}
                    </p>
                </div>
                <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                    <button
                        onClick={() => !isTransactionInitialized && decreaseQuantity(item.id)}
                        className={`border border-gray-300 w-6 h-6 flex items-center justify-center rounded ${isTransactionInitialized ? "text-gray-300 cursor-not-allowed" : "text-gray-500"}`}
                        disabled={isTransactionInitialized}
                        aria-label="Decrease quantity"
                    >
                        -
                    </button>
                    <span className="mx-1 w-4 text-center">{item.quantity}</span>
                    <button
                        onClick={() => !isTransactionInitialized && addToCart(item.id)}
                        className={`border border-gray-300 w-6 h-6 flex items-center justify-center rounded ${isTransactionInitialized ? "text-gray-300 cursor-not-allowed" : "text-gray-500"}`}
                        disabled={isTransactionInitialized}
                        aria-label="Increase quantity"
                    >
                        +
                    </button>
                    <button
                        onClick={() => !isTransactionInitialized && removeFromCart(item.id)}
                        className={`ml-2 hover:text-red-500 ${isTransactionInitialized ? "text-gray-300 cursor-not-allowed" : "text-gray-400"}`}
                        aria-label="Remove item"
                        disabled={isTransactionInitialized}
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            </div>
            {where === "dashboard" && !isTransactionInitialized && showDeliveryOptions && (
                <div className="mt-3 space-y-2">
                    {loadingCenters ? (
                        <p className="text-sm text-gray-500">Loading centers...</p>
                    ) : centers.length === 0 ? (
                        <p className="text-sm text-red-500">No SACA centers available.</p>
                    ) : (
                        <CartItemDeliveryFields
                            item={item}
                            deliveryInfo={deliveryInfo}
                            centers={centers}
                            handleDeliveryChange={handleDeliveryChange}
                            handleAddressChange={handleAddressChange}
                            handlePitchChange={handlePitchChange}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

const CartItemDeliveryFields: React.FC<{
    item: Product & { quantity: number };
    deliveryInfo: DeliveryOption;
    centers: Record<string, unknown>[];
    handleDeliveryChange: (id: number, type: "pickup" | "delivery") => void;
    handleAddressChange: (id: number | string, addr: string) => void;
    handlePitchChange: (id: number, pitchId: string) => void;
}> = ({ item, deliveryInfo, centers, handleDeliveryChange, handleAddressChange, handlePitchChange }) => {
    return (
        <>
            <label className="flex items-center space-x-2">
                <input
                    type="radio"
                    name={`delivery-${item.id}`}
                    value="delivery"
                    checked={deliveryInfo.delivery_type === "delivery"}
                    onChange={() => handleDeliveryChange(item.id, "delivery")}
                    className="w-4 h-4 text-[#317333] border-gray-300 focus:ring-[#317333]"
                />
                <span className="flex items-center space-x-2">
                    {deliveryInfo.delivery_type === "delivery" && <img src={check} className="w-4 h-4" alt="Checkmark" />}
                    <span className="text-sm text-gray-700">Deliver to my address</span>
                </span>
            </label>
            {deliveryInfo.delivery_type === "delivery" && (
                <AddressInput
                    item={item}
                    deliveryInfo={deliveryInfo}
                    handleAddressChange={handleAddressChange}
                />
            )}
            <label className="flex items-center space-x-2">
                <input
                    type="radio"
                    name={`delivery-${item.id}`}
                    value="pickup"
                    checked={deliveryInfo.delivery_type === "pickup"}
                    onChange={() => handleDeliveryChange(item.id, "pickup")}
                    className="w-4 h-4 text-[#317333] border-gray-300 focus:ring-[#317333]"
                />
                <span className="flex items-center space-x-2">
                    {deliveryInfo.delivery_type === "pickup" && <img src={check} className="w-4 h-4" alt="Checkmark" />}
                    <span className="text-sm text-gray-700">Pick up at SACA center (Free)</span>
                </span>
            </label>
            {deliveryInfo.delivery_type === "pickup" && (
                <select
                    value={deliveryInfo.pitch_id || ""}
                    onChange={(e) => handlePitchChange(item.id, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#317333]"
                >
                    <option value="" disabled>Select a SACA center</option>
                    {centers.map((center) => (
                        <option key={String(center.id)} value={String(center.id)}>{String(center.name)}</option>
                    ))}
                </select>
            )}
        </>
    );
};

const CardPaymentSection: React.FC<{
    showPayform: boolean;
    setShowPayform: (val: boolean) => void;
    clientSecret: string;
    stripePromise: Promise<import("@stripe/stripe-js").Stripe | null> | null;
    savedCards: Card[];
    transactionID: string;
    payment_method: string;
    currencyDetails: { currencyCode?: string | undefined; countryCode?: string | undefined };
    resetAll: () => void;
}> = ({
    showPayform, setShowPayform, clientSecret, stripePromise,
    savedCards, transactionID, payment_method, currencyDetails, resetAll
}) => {
        const finalizeStripePayment = async (result: unknown) => {
            const payload = typeof result === "object" && result !== null
                ? result as Record<string, unknown>
                : {};
            const intentValue = payload.payment_intent;
            const paymentIntentId = typeof intentValue === "string"
                ? intentValue
                : typeof intentValue === "object" && intentValue !== null
                    ? String((intentValue as Record<string, unknown>).id || "")
                    : "";

            if (!paymentIntentId) {
                return;
            }

            const reconfirmation = await transactionReconfirmationService.handleStripeSuccess(
                transactionID,
                { id: paymentIntentId }
            );

            if (reconfirmation.success) {
                resetAll();
                return;
            }

            if (reconfirmation.pending && !reconfirmation.requiresAction) {
                const pollResult = await transactionReconfirmationService.pollTransactionStatus(
                    transactionID,
                    { maxAttempts: 15, interval: 2000 }
                );
                if (pollResult.success) {
                    resetAll();
                }
            }
        };

        return (
            <>
                {showPayform ? (
                    clientSecret !== "" && (
                        <Elements options={{ clientSecret }} stripe={stripePromise}>
                            <Payform
                                clientSecret={clientSecret}
                                transactionId={transactionID}
                                payment_method={payment_method}
                                onSuccess={finalizeStripePayment}
                            />
                        </Elements>
                    )
                ) : (
                    clientSecret !== "" && (
                        <Elements options={{ clientSecret }} stripe={stripePromise}>
                            <SavedCards
                                savedCards={savedCards}
                                transactionID={transactionID}
                                clientSecret={clientSecret}
                                payment_method={payment_method}
                                onPaymentSuccess={finalizeStripePayment}
                                {...((currencyDetails.currencyCode) ? { currencyFilter: currencyDetails.currencyCode } : {})}
                                {...((currencyDetails.countryCode) ? { countryCodeFilter: currencyDetails.countryCode } : {})}
                                setModal={() => { }}
                            />
                        </Elements>
                    )
                )}

                {!showPayform && (
                    <button
                        className="w-full bg-[#317333] rounded-md py-3 font-Manrope text-[#fff] font-semibold text-sm md:text-base flex justify-center mt-4 items-center"
                        onClick={() => setShowPayform(true)}
                    >
                        Add New Card
                    </button>
                )}
            </>
        );
    };

export default Cart;
